import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../utils/api';
import MyBidsPreview from '../components-dashboard/MyBidsPreview';
import OverviewSection from '../components-dashboard/OverviewSection';
import BiddingActivityChart from '../components-dashboard/BiddingActivityChart';
import AutoBidsHistoryChart from '../components-dashboard/AutoBidsHistoryChart';
import ControlPanel from '../components-dashboard/ControlPanel';

const Dashboard = () => {
  // Get props from MainLayout via outlet context
  const { selectedUser, updatingSubUser, handleToggleAutoBid, handleAutoBidTypeChange } = 
    useOutletContext() || {};

  // State for total bids count
  const [totalBidsCount, setTotalBidsCount] = useState(0);
  const [loadingBidsCount, setLoadingBidsCount] = useState(false);

  // Fetch total bids count
  const fetchBidsCount = useCallback(async () => {
    try {
      setLoadingBidsCount(true);
      const url = `${(API_BASE || '').replace(/\/$/, '')}/bids?page=1&offset=1`;
      const headers = getAuthHeaders();
      const res = await axios.get(url, { headers, validateStatus: () => true });
      
      if (res.status >= 200 && res.status < 300) {
        const pagination = res.data?.pagination || res.data?.meta || res.data?.paging || null;
        if (pagination) {
          const total = pagination.total ?? pagination.total_count ?? pagination.totalCount ?? pagination.count;
          setTotalBidsCount(Number.isFinite(Number(total)) ? Number(total) : 0);
        } else {
          const bids = Array.isArray(res.data?.data) ? res.data.data : [];
          setTotalBidsCount(bids.length);
        }
      }
    } catch (err) {
      console.error('Failed to fetch bids count:', err);
      setTotalBidsCount(0);
    } finally {
      setLoadingBidsCount(false);
    }
  }, []);

  // Fetch bids count on mount
  useEffect(() => {
    fetchBidsCount();
  }, [fetchBidsCount]);

  const stats = {
    totalBids: totalBidsCount,
    loadingBids: loadingBidsCount,
    limitAlert: {
      message: 'No alerts at this time',
    },
  };

  return (
    <div className="space-y-6">
      {/* Overview Section Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold text-gray-900">Overview</h1>
        <ControlPanel />
      </div>

      {/* Overview and Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 flex flex-col gap-2.5">
          <OverviewSection stats={stats} />
          <BiddingActivityChart />
        </div>

        <div className="lg:col-span-2 flex flex-col gap-6">
          <AutoBidsHistoryChart
            autoBidEnabled={selectedUser?.autobid_enabled === true}
            onToggleAutoBid={handleToggleAutoBid}
            autoBidType={selectedUser?.autobid_enabled_for_job_type || 'all'}
            onAutoBidTypeChange={handleAutoBidTypeChange}
            updating={updatingSubUser}
          />
        </div>
      </div>

      {/* My Bids Preview */}
      <MyBidsPreview />
    </div>
  );
};

export default Dashboard;
